import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { THREE_PL_EXCEL_REPORTS } from '../../models/reports';
import { formatShipDateRangeForDisplay } from '../../utils/formatDisplayDate';
import { getStoredExportRemainingMs, type StoredThreePlExport } from '../../utils/pendingExportStorage';

interface ExportCompletedPanelProps {
  activeExport: StoredThreePlExport;
  onDownload: () => void;
  onDismiss: () => void;
}

export function ExportCompletedPanel({ activeExport, onDownload, onDismiss }: ExportCompletedPanelProps) {
  const { t, i18n } = useTranslation('threePlExcelReportsModule');

  const contextLabel = useMemo(() => {
    if (activeExport.reportId === 'active_clients') {
      return t('progress.active_clients_context');
    }

    if (activeExport.invoiceId != null) {
      const invoice = activeExport.invoiceLabel || `#${activeExport.invoiceId}`;
      return t('progress.invoice_label', { invoice });
    }

    if (!activeExport.dateFrom || !activeExport.dateTo) {
      return null;
    }

    const displayRange = formatShipDateRangeForDisplay(activeExport.dateFrom, activeExport.dateTo, i18n.language);
    return activeExport.dateFrom === activeExport.dateTo
      ? t('progress.date_range_single', { date: displayRange.from })
      : t('progress.date_range', { from: displayRange.from, to: displayRange.to });
  }, [activeExport, i18n.language, t]);

  const reportDef = THREE_PL_EXCEL_REPORTS.find((r) => r.id === activeExport.reportId);
  const remainingMin = Math.max(1, Math.ceil(getStoredExportRemainingMs(activeExport) / 60_000));

  return (
    <div
      className="rounded-lg border border-border bg-card px-4 py-3 transition-colors"
      data-testid="three-pl-export-completed-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-medium text-foreground">
            {reportDef ? t(reportDef.titleKey) : t('completed.title')}
          </p>
          <p className="text-xs text-muted-foreground">{contextLabel}</p>
          <p className="text-xs text-muted-foreground">{t('completed.available_hint', { minutes: remainingMin })}</p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss} data-testid="three-pl-export-dismiss">
            {t('completed.dismiss')}
          </Button>
          <Button type="button" size="sm" onClick={onDownload} data-testid="three-pl-export-download">
            {t('completed.download')}
          </Button>
        </div>
      </div>
    </div>
  );
}
